import { useEffect } from "react";
import NewOrleansHeroSection from "./components/HeroSection";
import NewOrleansStickyNav from "./components/StickyNav";
import NewOrleansProcessingTimeSection from "./components/ProcessingTimeSection";
import NewOrleansServicesSection from "./components/ServicesSection";
import NewOrleansLocalAreaSection from "./components/LocalAreaSection";
import NewOrleansTravelTimingSection from "./components/TravelTimingSection";
import NewOrleansDestinationsSection from "./components/DestinationsSection";
import NewOrleansTestimonialsSection from "./components/TestimonialsSection";
import NewOrleansAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import NewOrleansComparisonSection from "./components/ComparisonSection";
import NewOrleansMistakesSection from "./components/MistakesSection";
import NewOrleansHowItWorksSection from "./components/HowItWorksSection";
import NewOrleansFAQSection, { faqs } from "./components/FAQSection";
import NewOrleansHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import NewOrleansFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function NewOrleansPage() {
  useEffect(() => {
    document.title = "Emergency Passport New Orleans, LA — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/new-orleans/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in New Orleans, LA — as fast as 24 hours. Cruise, study-abroad, and oil industry travelers served. A+ BBB rated, registered State Dept. courier serving Greater New Orleans, Metairie, Kenner, and Southeast Louisiana.");
    setMeta("geo.placename", "New Orleans, Louisiana");
    setMeta("geo.region", "US-LA");
    setMeta("geo.position", "29.9511;-90.0715");
    setMeta("ICBM", "29.9511, -90.0715");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens." },
      { name: "New Passport", description: "First-time passport application assistance." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service." },
      { name: "Child Passport", description: "Passport services for minors under 16." },
      { name: "Name Change", description: "Passport correction and name change service." },
      { name: "Second Passport & Damaged", description: "Second passport issuance and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Louisiana", item: "https://fastpassportcenter.com/louisiana/" },
          { "@type": "ListItem", position: 3, name: "New Orleans Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for New Orleans and Southeast Louisiana travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "New Orleans", containedInPlace: { "@type": "State", name: "Louisiana" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "New Orleans" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <NewOrleansHeroSection />
      <NewOrleansStickyNav />
      <NewOrleansProcessingTimeSection />
      <NewOrleansServicesSection />
      <NewOrleansLocalAreaSection />
      <NewOrleansTravelTimingSection />
      <NewOrleansDestinationsSection />
      <NewOrleansTestimonialsSection />
      <NewOrleansAcceptanceFacilitiesSection />
      <NewOrleansComparisonSection />
      <NewOrleansMistakesSection />
      <NewOrleansHowItWorksSection />
      <NewOrleansFAQSection />
      <NewOrleansHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/new-orleans" state="LA" />
      <NewOrleansFinalCTA />
    </div>
  );
}